import { useState, useRef, useEffect, useMemo } from 'react';
import type { PricelistItem } from '@recotex/shared';
import { fmtEUR } from '../lib/format';

interface PricelistItemPickerProps {
  items: PricelistItem[];
  /** Optionele suggesties die bovenaan getoond worden vóór de generieke lijst. */
  suggestions?: { item: PricelistItem; score: number }[];
  value: string;
  onChange: (plItemId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Compact = kleinere inputs voor in dichte tabellen. */
  size?: 'normal' | 'sm';
}

/**
 * Searchable combobox over de prijslijst-items. Toont een dropdown met fuzzy-filter
 * op naamArbeid + pdfNaam tijdens typen. Bedoeld voor matching-flow waar je niet
 * door 250+ items wil scrollen.
 */
export function PricelistItemPicker({
  items,
  suggestions = [],
  value,
  onChange,
  placeholder = 'Zoek arbeid…',
  disabled,
  size = 'normal',
}: PricelistItemPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => items.find((it) => it.id === value), [items, value]);

  // Toon de naam van het geselecteerde item in de input zolang gebruiker niet typt.
  const displayValue = open ? query : selected ? `${selected.naamArbeid} — ${fmtEUR(selected.prijs)}/${selected.eenheid}` : '';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Geen zoekterm: suggesties bovenaan, dan eerste 50 items.
      const sugIds = new Set(suggestions.map((s) => s.item.id));
      const rest = items.filter((it) => !sugIds.has(it.id)).slice(0, 50);
      return [
        ...suggestions.map((s) => ({ item: s.item, hint: `${(s.score * 100).toFixed(0)}% match` })),
        ...rest.map((it) => ({ item: it, hint: undefined as string | undefined })),
      ];
    }
    const matches: Array<{ item: PricelistItem; hint?: string }> = [];
    for (const it of items) {
      const nA = it.naamArbeid.toLowerCase();
      const nP = it.pdfNaam.toLowerCase();
      if (nA.includes(q) || nP.includes(q)) {
        matches.push({ item: it });
        if (matches.length >= 50) break;
      }
    }
    return matches;
  }, [items, suggestions, query]);

  // Reset highlight bij query-verandering
  useEffect(() => { setHighlightIdx(0); }, [query]);

  // Sluit dropdown bij klik buiten
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const choose = (it: PricelistItem) => {
    onChange(it.id);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      setHighlightIdx((i) => Math.min(filtered.length - 1, i + 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlightIdx((i) => Math.max(0, i - 1));
      e.preventDefault();
    } else if (e.key === 'Enter') {
      const pick = filtered[highlightIdx]?.item;
      if (pick) choose(pick);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const inputCls = size === 'sm'
    ? 'w-full px-3 py-2 text-sm bg-white border border-border rounded-md focus:outline-none focus:border-primary focus:shadow-focus transition-shadow'
    : 'input';

  return (
    <div ref={wrapperRef} className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        className={inputCls}
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {selected && !open && (
        <button
          type="button"
          tabIndex={-1}
          onClick={(e) => { e.stopPropagation(); onChange(''); setQuery(''); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-secondary hover:text-danger px-1"
          title="Selectie wissen"
        >✕</button>
      )}
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto bg-white border border-border rounded-md shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-sm text-secondary">Geen resultaten voor "{query}"</div>
          ) : (
            <ul role="listbox" className="py-1">
              {filtered.map(({ item: it, hint }, idx) => {
                const isHigh = idx === highlightIdx;
                return (
                  <li
                    key={it.id}
                    role="option"
                    aria-selected={isHigh}
                    onMouseDown={(e) => { e.preventDefault(); choose(it); }}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    className={`px-3 py-2 cursor-pointer text-sm flex justify-between gap-3 ${isHigh ? 'bg-primary-50' : ''}`}
                  >
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold text-primary block truncate">{it.naamArbeid}</span>
                      <span className="text-xs text-secondary block truncate">{it.pdfNaam}</span>
                    </span>
                    <span className="text-right whitespace-nowrap shrink-0">
                      <span className="font-bold tabular-nums">{fmtEUR(it.prijs)}</span>
                      <span className="text-secondary text-xs"> /{it.eenheid}</span>
                      {hint && <span className="block text-[10px] text-accent">{hint}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
